import {
  deleteInvitationAsDoctorMutation,
  sendInvitationReminderMutation,
} from '@/api/invitations/invitationMutations';
import type { InvitationsFilters } from '@/api/invitations/invitationQueries';
import {
  getInvitationsAsDoctorQuery,
  getInvitationsQuery,
} from '@/api/invitations/invitationQueries';
import { queryKeys } from '@/api/queryKeys';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { formatDate, isOlderThanThreeDays } from '@/lib/utils/dateUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../DashboardTableAccordion';
import { ModifyUserCart } from './show-patient/ModifyUserCart';
import { ShowInvitedPatientsFilters } from './ShowInvitedPatientsFilters';

export default function ShowInvitedPatients() {
  const queryClient = useQueryClient();
  const { hasRole } = useHasRole();
  const isDoctor = hasRole(Role.DOCTOR);
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uuidToDelete, setUuidToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<InvitationsFilters>({});
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    name: string;
    surname: string;
    email: string;
  } | null>(null);

  const { data: response, isLoading } = useQuery(
    isDoctor
      ? getInvitationsAsDoctorQuery(page, filters)
      : getInvitationsQuery('Customer', page, filters),
  );

  const deleteInvitation = useMutation({
    ...deleteInvitationAsDoctorMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invitations.all(),
      });
      toast.success('Invitation deleted successfully');
    },
  });

  const sendReminder = useMutation({
    ...sendInvitationReminderMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invitations.all(),
      });
      toast.success('Reminder sent successfully');
    },
  });

  const invitations = response?.data || [];
  const paginationMeta = response?.meta;

  const handleDeleteClick = (uuid: string) => {
    setUuidToDelete(uuid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (uuidToDelete) {
      deleteInvitation.mutate(uuidToDelete);
      setDeleteDialogOpen(false);
      setUuidToDelete(null);
    }
  };

  const handleSendReminder = (uuid: string) => {
    sendReminder.mutate(uuid);
  };

  const handleCreateOrder = (invitation: {
    user_id?: number;
    user?: { id: number; name: string; surname: string; email: string };
    name: string;
    surname: string;
    email: string;
  }) => {
    const userId = invitation.user?.id ?? invitation.user_id;
    if (userId == null) return;
    setSelectedUser({
      id: userId,
      name: invitation.user?.name ?? invitation.name,
      surname: invitation.user?.surname ?? invitation.surname,
      email: invitation.user?.email ?? invitation.email,
    });
    setIsCreateOrderOpen(true);
  };

  return (
    <>
      <DashboardTableAccordion
        value="Show invited patients"
        title="Show invited patients"
        data={invitations.map((inv) => ({ ...inv, id: inv.uuid }))}
        isLoading={isLoading}
        emptyMessage="No invitations found."
        loadingMessage="Loading invitations..."
        paginationMeta={paginationMeta}
        onPageChange={setPage}
        filtersComponent={
          <ShowInvitedPatientsFilters
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
          />
        }
        renderHeader={() => (
          <>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Invited by</TableHead>
            <TableHead>Date</TableHead>
            <TableHead></TableHead>
            <TableHead></TableHead>
            <TableHead></TableHead>
          </>
        )}
        renderRow={(invitation) => {
          const isStale = isOlderThanThreeDays(invitation.created_at);
          const nameEmailDateClass = isStale
            ? 'text-delete'
            : 'text-table-foreground';
          return (
            <>
              <TableCell className={nameEmailDateClass}>
                {invitation.name} {invitation.surname}
              </TableCell>
              <TableCell className={nameEmailDateClass}>
                {invitation.email}
              </TableCell>
              <TableCell className={nameEmailDateClass}>
                {invitation.inviter.name} {invitation.inviter.surname}
              </TableCell>
              <TableCell className={nameEmailDateClass}>
                {formatDate(invitation.created_at)}
              </TableCell>
              <TableCell>
                <RelationActionButton
                  onClick={() => handleCreateOrder(invitation)}
                  disabled={!(invitation.user?.id ?? invitation.user_id)}
                >
                  Create order
                </RelationActionButton>
              </TableCell>
              <TableCell>
                <RelationActionButton
                  onClick={() => handleSendReminder(invitation.uuid)}
                  disabled={sendReminder.isPending}
                >
                  Send reminder
                </RelationActionButton>
              </TableCell>
              <TableCell className="text-right">
                <RelationActionButton
                  onClick={() => handleDeleteClick(invitation.uuid)}
                  className="text-delete"
                  disabled={deleteInvitation.isPending}
                >
                  Delete
                </RelationActionButton>
              </TableCell>
            </>
          );
        }}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete invitation"
        description="Are you sure you want to delete this invitation?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteInvitation.isPending}
      />

      <ModifyUserCart
        key={selectedUser?.id ?? 'empty'}
        open={isCreateOrderOpen}
        onOpenChange={setIsCreateOrderOpen}
        selectedUser={selectedUser}
        initialAdding={true}
      />
    </>
  );
}
